import React from 'react';
import { Flame, ShieldCheck } from 'lucide-react';

/**
 * ModeAestheticPreview — visual color-swatch + sample-pill preview of the
 * two app modes (Athlete / Adaptive). Used in Onboarding's `mode` step AND
 * in Quickstart's `mode` step so the choice is tangible in both flows.
 *
 * Renders hex values DIRECTLY instead of using theme tokens — so the
 * preview always shows what each mode LOOKS LIKE regardless of what mode
 * the user is currently in. (Critical: if a user in Adaptive Mode opens
 * this preview, the Athlete card must still look like Athlete.)
 *
 * Props:
 *   variant — 'athlete' | 'adaptive'  (which preview to render)
 *
 * Designed to slot in beneath the mode-name + description block, sized
 * compactly enough for both desktop side-by-side and mobile stacked layouts.
 */
const ModeAestheticPreview = ({ variant }) => {
  if (variant === 'athlete') {
    return (
      <div
        className="rounded-lg border p-3 space-y-2.5"
        style={{ borderColor: '#27272a', backgroundColor: '#09090b' }}
        data-testid="mode-preview-athlete"
      >
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-[0.25em] font-semibold" style={{ color: '#9CA3AF' }}>
            Aesthetic
          </span>
          <div className="flex gap-1.5 ml-auto">
            <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#09090b', border: '1px solid #27272a' }} title="surface" />
            <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#dc2626' }} title="iron-red accent" />
            <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#f97316' }} title="warm" />
            <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#ffffff' }} title="text" />
          </div>
        </div>
        <div
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border"
          style={{ borderColor: '#dc2626', backgroundColor: 'rgba(220,38,38,0.1)' }}
        >
          <Flame className="w-3.5 h-3.5 scale-x-[-1]" style={{ color: '#dc2626' }} />
          <span className="text-xs font-semibold" style={{ color: '#ffffff' }}>
            Today's workout · 4 of 7
          </span>
        </div>
      </div>
    );
  }

  // Adaptive
  return (
    <div
      className="rounded-lg border p-3 space-y-2.5"
      style={{ borderColor: '#D9CFB8', backgroundColor: '#FFFFFF' }}
      data-testid="mode-preview-adaptive"
    >
      <div className="flex items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.25em] font-semibold" style={{ color: '#6B7280' }}>
          Aesthetic
        </span>
        <div className="flex gap-1.5 ml-auto">
          <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#F5EFE3', border: '1px solid #D9CFB8' }} title="sand surface" />
          <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#b8faf0' }} title="mint accent" />
          <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#ccb1f1' }} title="lavender" />
          <span className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#C97B5A' }} title="clay warm" />
        </div>
      </div>
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border"
        style={{ borderColor: '#b8faf0', backgroundColor: '#F5FCFA' }}
      >
        <ShieldCheck className="w-3.5 h-3.5" style={{ color: '#6B9A93' }} />
        <span className="text-xs font-semibold" style={{ color: '#1F2937' }}>
          Steady today · 4 of last 7
        </span>
      </div>
    </div>
  );
};

export default ModeAestheticPreview;
